import { z } from 'zod';

// スキーマのエラーメッセージは外部から注入する
type CreateResetPasswordSchemaMessages = {
  RequiredMessage: string;
  NoFullWidthMessage: string;
  MaxLengthMessage: (max: number) => string;
};

type CreateChangePasswordSchemaMessages = {
  RequiredMessage: string;
  PasswordConfirmationMessage: string;
  SpecialCharacterMessage: string;
  UppercaseMessage: string;
  LowercaseMessage: string;
  NoFullWidthMessage: string;
  NumericMessage: string;
  PasswordNumericMessage: string;
  MinLengthMessage: (min: number) => string;
  MaxLengthMessage: (max: number) => string;
  LengthMessage: (length: number) => string;
};

export const CreateResetPasswordSchema = (messages: CreateResetPasswordSchemaMessages) =>
  z.object({
    id: z
      .string()
      .min(1, messages.RequiredMessage)
      .max(100, messages.MaxLengthMessage(100))
      .regex(
        /^[^\uFF01-\uFF5E\u3000-\u303F\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FFF]*$/,
        messages.NoFullWidthMessage
      ),
  });
export type CreateResetPasswordType = z.infer<ReturnType<typeof CreateResetPasswordSchema>>;

export const CreateConfirmResetPasswordSchema = (messages: CreateChangePasswordSchemaMessages) =>
  z
    .object({
      id: z
        .string()
        .min(1, messages.RequiredMessage)
        .max(100, messages.MaxLengthMessage(100))
        .regex(
          /^[^\uFF01-\uFF5E\u3000-\u303F\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FFF]*$/,
          messages.NoFullWidthMessage
        ),
      code: z
        .string()
        .min(1, messages.RequiredMessage)
        .length(6, messages.LengthMessage(6))
        .regex(/^[0-9]+$/, messages.NumericMessage),
      password: z
        .string()
        .min(1, messages.RequiredMessage)
        .min(12, messages.MinLengthMessage(12))
        .max(100, messages.MaxLengthMessage(100))
        .regex(
          /^[^\uFF01-\uFF5E\u3000-\u303F\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FFF]*$/,
          messages.NoFullWidthMessage
        )
        .regex(/[\^$*.[\]{}()?"!@#%&/\\,><':;|_~`=+-]/, messages.SpecialCharacterMessage)
        .regex(/[A-Z]/, messages.UppercaseMessage)
        .regex(/[a-z]/, messages.LowercaseMessage)
        .regex(/[0-9]/, messages.PasswordNumericMessage),
      passwordConfirmation: z.string().min(1, messages.RequiredMessage),
    })
    .superRefine(({ password, passwordConfirmation }, ctx) => {
      if (password !== passwordConfirmation) {
        ctx.addIssue({
          path: ['passwordConfirmation'],
          code: 'custom',
          message: messages.PasswordConfirmationMessage,
        });
      }
    });
export type CreateConfirmResetPasswordType = z.infer<
  ReturnType<typeof CreateConfirmResetPasswordSchema>
>;

export const CreateChangePasswordSchema = (messages: CreateChangePasswordSchemaMessages) =>
  z
    .object({
      oldPassword: z
        .string()
        .min(1, messages.RequiredMessage)
        .max(100, messages.MaxLengthMessage(100))
        .regex(
          /^[^\uFF01-\uFF5E\u3000-\u303F\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FFF]*$/,
          messages.NoFullWidthMessage
        ),
      newPassword: z
        .string()
        .min(1, messages.RequiredMessage)
        .min(12, messages.MinLengthMessage(12))
        .max(100, messages.MaxLengthMessage(100))
        .regex(
          /^[^\uFF01-\uFF5E\u3000-\u303F\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FFF]*$/,
          messages.NoFullWidthMessage
        )
        .regex(/[\^$*.[\]{}()?"!@#%&/\\,><':;|_~`=+-]/, messages.SpecialCharacterMessage)
        .regex(/[A-Z]/, messages.UppercaseMessage)
        .regex(/[a-z]/, messages.LowercaseMessage)
        .regex(/[0-9]/, messages.PasswordNumericMessage),
      passwordConfirmation: z.string().min(1, messages.RequiredMessage),
    })
    .superRefine(({ newPassword, passwordConfirmation }, ctx) => {
      if (newPassword !== passwordConfirmation) {
        ctx.addIssue({
          path: ['passwordConfirmation'],
          code: 'custom',
          message: messages.PasswordConfirmationMessage,
        });
      }
    });
export type CreateChangePasswordFormType = z.infer<ReturnType<typeof CreateChangePasswordSchema>>;
